import { useState, useEffect } from 'react';
import { TrendingDown, ArrowRight, Globe, Megaphone, Mail, Send, Loader2, CheckCircle2, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = '';

function FunnelVisualization({ data }) {
  if (!data) return null;
  const { steps, dropoffs, summary } = data;
  const maxCount = Math.max(...steps.map(s => s.count), 1);

  const STEP_LABELS = {
    page_view: 'Page Views', scan_started: 'Scans Started', step_completed: 'Steps Completed',
    scan_completed: 'Scans Completed', preview_viewed: 'Previews Viewed', payment_clicked: 'Payment Clicked',
    payment_completed: 'Payments', report_viewed: 'Reports Viewed',
  };

  const keySteps = steps.filter(s => ['scan_started', 'scan_completed', 'preview_viewed', 'payment_clicked', 'payment_completed'].includes(s.step));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="p-3 bg-white rounded-lg border border-border text-center">
          <p className="text-xs text-muted-foreground">Total Events</p>
          <p className="text-2xl font-medium">{summary.total_events}</p>
        </div>
        <div className="p-3 bg-white rounded-lg border border-border text-center">
          <p className="text-xs text-muted-foreground">Reports Created</p>
          <p className="text-2xl font-medium">{summary.total_reports}</p>
        </div>
        <div className="p-3 bg-white rounded-lg border border-border text-center">
          <p className="text-xs text-muted-foreground">With UTM Source</p>
          <p className="text-2xl font-medium">{summary.reports_with_utm}</p>
        </div>
      </div>

      {/* Funnel bars */}
      <div className="bg-white rounded-lg border border-border p-5">
        <h4 className="text-sm font-semibold mb-4">Conversion Funnel</h4>
        <div className="space-y-2">
          {keySteps.map(s => (
            <div key={s.step} className="flex items-center gap-3">
              <span className="text-xs w-32 text-right truncate">{STEP_LABELS[s.step] || s.step}</span>
              <div className="flex-1 bg-muted rounded-full h-7 overflow-hidden">
                <div
                  className="bg-primary/70 h-full rounded-full flex items-center justify-end pr-2 transition-all"
                  style={{ width: `${Math.max((s.count / maxCount) * 100, 4)}%` }}
                >
                  <span className="text-[10px] text-white font-medium">{s.count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Drop-offs */}
      {dropoffs.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="w-4 h-4 text-amber-600" />
            <h4 className="text-sm font-semibold">Drop-off Analysis</h4>
          </div>
          <div className="space-y-3">
            {dropoffs.map(d => (
              <div key={`${d.from}-${d.to}`} className="flex items-center gap-3 text-xs">
                <span className="w-28 text-right font-medium">{STEP_LABELS[d.from] || d.from}</span>
                <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                <span className="w-28 font-medium">{STEP_LABELS[d.to] || d.to}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${d.conversion_rate >= 30 ? 'bg-green-100 text-green-700' : d.conversion_rate >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                  {d.conversion_rate}% convert
                </span>
                <span className="text-muted-foreground">{d.from_count} → {d.to_count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AttributionTable({ title, icon: Icon, data, columns }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="bg-white rounded-lg border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-primary" />
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            {columns.map(c => <th key={c.key} className={`pb-2 pr-4 ${c.align === 'right' ? 'text-right' : ''}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i} className="border-b border-border/50">
              {columns.map(c => (
                <td key={c.key} className={`py-2 pr-4 ${c.align === 'right' ? 'text-right font-medium' : ''}`}>
                  {row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FollowUpPanel({ token }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/followup/stats`, opts);
      setStats(data.followup);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { if (token) loadStats(); }, [token]);

  const triggerSequence = async (seq) => {
    setSending(seq);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/followup/trigger`, { sequence: seq }, opts);
      toast.success(`Sent: ${data.sent}, Skipped: ${data.skipped}, Errors: ${data.errors}`);
      await loadStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
    setSending(null);
  };

  const handlePreview = async (seqId) => {
    setLoadingPreview(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/followup/preview/${seqId}?lang=nl`, opts);
      setPreview({ id: seqId, subject: data.subject, html: data.html });
    } catch { toast.error('Preview failed'); }
    setLoadingPreview(false);
  };

  if (loading) return <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>;
  if (!stats) return null;

  const sequences = [
    { id: 'non_buyer_nudge', label: 'Non-Buyer Nudge', desc: 'Remind locked-report users (>24h old) about their savings', icon: '📩', data: stats.non_buyer_nudge },
    { id: 'unlocked_next_steps', label: 'Next Steps Email', desc: 'Send top 3 action steps to unlocked-report users (>3 days)', icon: '📋', data: stats.unlocked_next_steps },
    { id: 'quote_confirmation', label: 'Quote Confirmation', desc: 'Confirm receipt of new quote requests', icon: '✅', data: stats.quote_confirmation },
  ];

  return (
    <div className="space-y-4" data-testid="followup-panel">
      <h4 className="text-sm font-semibold">Follow-up Email Sequences</h4>
      <div className="grid md:grid-cols-3 gap-4">
        {sequences.map(seq => (
          <div key={seq.id} className="bg-white rounded-lg border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <span>{seq.icon}</span>
              <h5 className="text-xs font-semibold">{seq.label}</h5>
            </div>
            <p className="text-[10px] text-muted-foreground mb-3">{seq.desc}</p>
            <div className="flex items-center justify-between text-xs mb-3">
              <span className="text-muted-foreground">Eligible: <span className="font-medium text-foreground">{seq.data.eligible}</span></span>
              <span className="text-muted-foreground">Sent: <span className="font-medium text-green-700">{seq.data.sent}</span></span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePreview(seq.id)}
                disabled={loadingPreview}
                className="flex-1 px-3 py-1.5 text-xs border border-border text-foreground rounded-md hover:bg-muted/50 disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid={`preview-${seq.id}`}
              >
                {loadingPreview ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                Preview
              </button>
              <button
                onClick={() => triggerSequence(seq.id)}
                disabled={sending === seq.id || seq.data.eligible === 0}
                className="flex-1 px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-1"
                data-testid={`trigger-${seq.id}`}
              >
                {sending === seq.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Send ({seq.data.eligible})
              </button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setPreview(null)} data-testid="email-preview-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-[640px] w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Email Preview</p>
                <p className="text-sm font-medium">{preview.subject}</p>
              </div>
              <button onClick={() => setPreview(null)} className="p-1 hover:bg-muted rounded" data-testid="close-preview">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe srcDoc={preview.html} title="Email preview" className="w-full h-[600px] border-0" sandbox="" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function FunnelTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/api/admin/analytics/funnel`, {
      headers: { Cookie: `access_token=${token}` },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(d => { setData(d.funnel); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) return <p className="text-sm text-muted-foreground py-8 text-center">Loading funnel data...</p>;

  return (
    <div className="space-y-6" data-testid="funnel-tab">
      <FunnelVisualization data={data} />

      {data && (
        <div className="grid md:grid-cols-2 gap-6">
          <AttributionTable
            title="Source Attribution"
            icon={Globe}
            data={data.source_attribution}
            columns={[
              { key: 'source', label: 'Source' },
              { key: 'scans_started', label: 'Scans', align: 'right' },
              { key: 'scans_completed', label: 'Completed', align: 'right' },
              { key: 'payments', label: 'Payments', align: 'right' },
            ]}
          />
          <AttributionTable
            title="Campaign Attribution"
            icon={Megaphone}
            data={data.campaign_attribution}
            columns={[
              { key: 'campaign', label: 'Campaign' },
              { key: 'scans_started', label: 'Scans', align: 'right' },
              { key: 'scans_completed', label: 'Completed', align: 'right' },
              { key: 'payments', label: 'Payments', align: 'right' },
            ]}
          />
        </div>
      )}

      {/* Follow-up emails */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-primary" />
          <h3 className="text-base font-semibold" style={{ fontFamily: 'Outfit' }}>Follow-up Emails</h3>
        </div>
        <FollowUpPanel token={token} />
      </div>
    </div>
  );
}
